import React from "react";
import * as Mui from "@mui/material";
import * as Icons from "react-feather";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { FormField, FormSelectField } from "component/shared/form-fields";
import { stepNumber } from "./step-number";
import { styles } from "./style";

// The fields a widget's "Customize Widget" block is made of (MAX-4403), so the fourteen forms share
// one layout: two columns of refreshed fields, with a full-width row where a field needs it.

interface GridProps {
	children?: React.ReactNode;
}

export const WidgetFieldGrid: React.FC<GridProps> = ({ children }) => (
	<Mui.Grid container spacing={2}>
		{children}
	</Mui.Grid>
);

interface CellProps {
	/** Takes the full row instead of half of it. */
	full?: boolean;
	children?: React.ReactNode;
}

export const WidgetFieldCell: React.FC<CellProps> = ({ full, children }) => (
	<Mui.Grid item xs={12} sm={full ? 12 : 6}>
		{children}
	</Mui.Grid>
);

interface GroupOwnProps {
	title: string;
	children?: React.ReactNode;
}

const GroupComponent: React.FC<GroupOwnProps & WithStyles<typeof styles>> = ({ classes = {}, title, children }) => (
	<div className={classes.group}>
		<Mui.Typography variant="h4" className={classes.groupTitle}>
			{title}
		</Mui.Typography>
		{children}
	</div>
);

/** A titled run of fields inside "Customize Widget", e.g. the Gallery Slider's Navigation. */
export const WidgetFieldGroup = withStyles(GroupComponent, styles);

export interface WidgetOption {
	value: string | number;
	label: string;
}

interface SelectProps {
	label: string;
	value: string | number | undefined;
	options: WidgetOption[];
	onChange: (value: string) => void;
	required?: boolean;
}

export const WidgetSelect: React.FC<SelectProps> = ({ label, value, options, onChange, required }) => (
	<FormSelectField
		label={label}
		required={required}
		value={value}
		options={options}
		valueExtractor={(option) => option.value}
		labelExtractor={(option) => option.label}
		onChange={(selected) => onChange(String(selected))}
	/>
);

interface NumberProps {
	label: string;
	/** NaN, as `parseInt("")` leaves it when the field is cleared, shows as empty. */
	value: number | undefined;
	onChange: (value: number) => void;
	required?: boolean;
	min?: number;
	max?: number;
	helperText?: string;
	/** Shown before the value, e.g. "$" on the price fields. */
	startAdornment?: React.ReactNode;
}

const NumberComponent: React.FC<NumberProps & WithStyles<typeof styles>> = ({
	classes = {},
	label,
	value,
	onChange,
	required,
	min,
	max,
	helperText,
	startAdornment,
}) => {
	const step = (direction: 1 | -1) => {
		const next = stepNumber(value, direction, min, max);
		if (next !== undefined) {
			onChange(next);
		}
	};
	return (
		<div className={classes.number}>
			<FormField
				label={label}
				type="number"
				required={required}
				min={min}
				max={max}
				value={value === undefined || Number.isNaN(value) ? "" : String(value)}
				onChange={(event) => onChange(parseInt(event.target.value))}
				error={!!helperText}
				helperText={helperText}
				startAdornment={startAdornment}
				// The Figma's up/down steppers, in place of the browser's own, which the wrapper hides.
				endAdornment={
					<span className={classes.stepper}>
						<Mui.IconButton
							size="small"
							className={classes.step}
							aria-label={`Increase ${label}`}
							onClick={() => step(1)}
						>
							<Icons.ChevronUp size={16} />
						</Mui.IconButton>
						<Mui.IconButton
							size="small"
							className={classes.step}
							aria-label={`Decrease ${label}`}
							onClick={() => step(-1)}
						>
							<Icons.ChevronDown size={16} />
						</Mui.IconButton>
					</span>
				}
			/>
		</div>
	);
};

export const WidgetNumber = withStyles(NumberComponent, styles);
